import React, { useState, useEffect } from "react";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Globe,
  DollarSign,
} from "lucide-react";
import { useBusiness } from "../contexts/BusinessContext";
import { formatBusinessEmail } from "../lib/email";
import { supabase } from "../lib/supabase";

export default function BusinessSettingsForm() {
  const { selectedBusiness, updateBusiness } = useBusiness();
  if (!selectedBusiness) return null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalFormData, setOriginalFormData] = useState({
    name: selectedBusiness?.name || "",
    type: selectedBusiness?.type || "",
    email: selectedBusiness?.email || "",
    business_domain_email: selectedBusiness?.business_domain_email || "",
    primary_email: selectedBusiness?.primary_email || "contact",
    email_verified: selectedBusiness?.email_verified || false,
    phone: selectedBusiness?.phone || "",
    address: selectedBusiness?.address || "",
    timezone: selectedBusiness?.timezone || "America/Vancouver",
    currency: selectedBusiness?.currency || "USD",
    gst_number: selectedBusiness?.gst_number || "",
    pst_number: selectedBusiness?.pst_number || "",
  });
  const [formData, setFormData] = useState({...originalFormData});
  const [isEditingDomainEmail, setIsEditingDomainEmail] = useState(false);
  
  // Track if form has changes
  const hasChanges = React.useMemo(() => {
    return (
      formData.name !== originalFormData.name ||
      formData.email !== originalFormData.email ||
      formData.business_domain_email !== originalFormData.business_domain_email ||
      formData.primary_email !== originalFormData.primary_email ||
      formData.phone !== originalFormData.phone ||
      formData.address !== originalFormData.address ||
      formData.timezone !== originalFormData.timezone ||
      formData.currency !== originalFormData.currency ||
      formData.gst_number !== originalFormData.gst_number ||
      formData.pst_number !== originalFormData.pst_number
    );
  }, [formData, originalFormData]);

  // Initialize form data on component mount or business change
  useEffect(() => {
    if (selectedBusiness) {
      const newData = {
        name: selectedBusiness.name || "",
        type: selectedBusiness.type || "",
        email: selectedBusiness.email || "",
        business_domain_email: selectedBusiness.business_domain_email || "",
        primary_email: selectedBusiness.primary_email || "contact",
        email_verified: selectedBusiness.email_verified || false,
        phone: selectedBusiness.phone || "",
        address: selectedBusiness.address || "",
        timezone: selectedBusiness.timezone || "America/Vancouver",
        currency: selectedBusiness.currency || "USD",
        gst_number: selectedBusiness.gst_number || "",
        pst_number: selectedBusiness.pst_number || "",
      };
      setOriginalFormData(newData);
      setFormData(newData);
    }
  }, [selectedBusiness]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Skip submission if no actual changes
    if (!hasChanges) {
      setLoading(false);
      return;
    }

    try {
      // Validate primary email selection
      if (formData.primary_email === "contact" && !formData.email_verified) {
        setError(
          "Contact email must be verified before it can be set as primary"
        );
        setLoading(false);
        return;
      }

      // Check if business_domain_email is unique
      if (formData.business_domain_email) {
        const { data: existingBusiness, error: checkError } = await supabase
          .from("businesses")
          .select("id")
          .eq("business_domain_email", formData.business_domain_email)
          .neq("id", selectedBusiness.id)
          .single();

        if (existingBusiness) {
          setError("This domain email is already in use by another business");
          setLoading(false);
          return;
        }
      }

      const { error } = await supabase
        .from("businesses")
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          business_domain_email: formData.business_domain_email,
          primary_email: formData.primary_email,
          timezone: formData.timezone,
          currency: formData.currency,
          business_hours: formData.business_hours,
          booking_settings: formData.booking_settings,
          notification_settings: formData.notification_settings,
          gst_number: formData.gst_number,
          pst_number: formData.pst_number,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedBusiness.id);

      if (error) throw error;

      setSuccess("Business settings updated successfully");
      setIsEditingDomainEmail(false);
      await updateBusiness(selectedBusiness.id, formData);
      // Update original form data to match current data after successful save
      setOriginalFormData({...formData});
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update business settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDomainEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric characters
    const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    setFormData((prev) => ({
      ...prev,
      business_domain_email: `${sanitizedValue}@${
        formatBusinessEmail("").split("@")[1]
      }`,
    }));
  };
  
  const resetForm = () => {
    setFormData({...originalFormData});
    setIsEditingDomainEmail(false);
    setError(null);
    setSuccess(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 md:px-4 md:py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="w-full">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Business name
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
          Business address
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, address: e.target.value }))
          }
          className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Contact Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Business Domain Email
          </label>
          {isEditingDomainEmail ? (
            <div className="relative">
              <input
                type="text"
                value={formData.business_domain_email?.split("@")[0] || ""}
                onChange={handleDomainEmailChange}
                className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm pr-24"
                placeholder="Enter username"
                pattern="[a-zA-Z0-9]*"
                title="Only letters and numbers"
                autoFocus
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-xs md:text-sm">
                @{formatBusinessEmail("").split("@")[1]}
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <div className="flex-1 h-10 px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-900 truncate">
                {formData.business_domain_email || "Not set"}
              </div>
              <button
                type="button"
                onClick={() => setIsEditingDomainEmail(true)}
                className="px-2 py-1.5 md:px-3 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Update
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
          Primary Email
        </label>
        <div className="space-y-2">
          <label className="flex items-start md:items-center space-x-2 md:space-x-3">
            <input
              type="radio"
              name="primary_email"
              value="contact"
              checked={formData.primary_email === "contact"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  primary_email: e.target.value,
                }))
              }
              className="h-4 w-4 mt-0.5 md:mt-0 text-gray-900 focus:ring-gray-900"
              disabled={!formData.email_verified}
            />
            <div className="flex flex-col md:flex-row md:items-center md:space-x-2">
              <span className="text-sm text-gray-700">
                Use Contact Email ({formData.email || "Not set"})
              </span>
              {!formData.email_verified && (
                <div className="flex items-center space-x-2 mt-1 md:mt-0">
                  <div className="relative group">
                    <svg
                      className="w-4 h-4 text-yellow-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      This email needs to be verified before it can be used as
                      primary. Click the verify button to send a verification
                      email.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        if (!supabase) {
                          throw new Error("Supabase client not initialized");
                        }

                        if (!formData.email) {
                          setError("Please enter an email address first");
                          return;
                        }

                        // Get the current ngrok URL from the window location
                        const ngrokUrl =
                          window.location.href.split("/settings")[0];

                        const { error } = await supabase.functions.invoke(
                          "verify-email",
                          {
                            body: {
                              email: formData.email,
                              businessId: selectedBusiness.id,
                              frontendUrl: ngrokUrl,
                            },
                          }
                        );

                        if (error) {
                          console.error("Verification error:", error);
                          throw new Error(
                            error.message || "Failed to send verification email"
                          );
                        }

                        setSuccess(
                          "Verification email sent! Please check your inbox."
                        );
                        setError(null);
                      } catch (err) {
                        console.error("Error sending verification email:", err);
                        setError(
                          err instanceof Error
                            ? err.message
                            : "Failed to send verification email"
                        );
                        setSuccess(null);
                      }
                    }}
                    className="text-xs md:text-sm text-blue-600 hover:text-blue-800 border border-gray-300 rounded-md px-2 py-1"
                  >
                    Verify
                  </button>
                </div>
              )}
            </div>
          </label>
          <label className="flex items-center space-x-2 md:space-x-3">
            <input
              type="radio"
              name="primary_email"
              value="domain"
              checked={formData.primary_email === "domain"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  primary_email: e.target.value,
                }))
              }
              className="h-4 w-4 text-gray-900 focus:ring-gray-900"
            />
            <span className="text-sm text-gray-700">
              Use Domain Email ({formData.business_domain_email || "Not set"})
            </span>
          </label>
        </div>
        <p className="mt-2 text-xs md:text-sm text-gray-500">
          The primary email will be used for customer communications and
          notifications. Domain emails are automatically verified.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Phone number
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, phone: e.target.value }))
            }
            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Time zone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, timezone: e.target.value }))
            }
            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
          >
            <option value="America/Vancouver">(GMT-08:00) Pacific Time</option>
            <option value="America/Denver">(GMT-07:00) Mountain Time</option>
            <option value="America/Chicago">(GMT-06:00) Central Time</option>
            <option value="America/New_York">(GMT-05:00) Eastern Time</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Currency
          </label>
          <select
            value={formData.currency}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, currency: e.target.value }))
            }
            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
          >
            <option value="USD">USD ($)</option>
            <option value="CAD">CAD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
            GST Number (optional)
          </label>
          <input
            type="text"
            value={formData.gst_number}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, gst_number: e.target.value }))
            }
            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
            placeholder="Enter GST number"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
          PST Number (optional)
        </label>
        <input
          type="text"
          value={formData.pst_number}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, pst_number: e.target.value }))
          }
          className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
          placeholder="Enter PST number"
        />
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-3 py-2 md:px-4 md:py-3 rounded-md text-sm">
          {success}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-center md:justify-end mt-4 gap-3">
        {hasChanges && (
          <button
            type="button"
            onClick={resetForm}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !hasChanges}
          className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            hasChanges && !loading
              ? 'bg-gray-900 hover:bg-gray-800 focus:ring-gray-900'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
